import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import html2pdf from "html2pdf.js";
import { useEffect, useMemo, useRef, useState } from "react";
import DataTable, { TableColumn } from "react-data-table-component";
import { useLocation, useNavigate } from "react-router-dom";
import { SpinnerCircular } from "spinners-react";
import { useSetStuffToRenderOnNavbar } from "../../contexts/NavbarCommonSpace";
import {
  useAllFields,
  useApplicationAdmin,
  useApplicationFormSteps,
  useGeneratedDocsAdmin,
  usePaymentReceiptsAdmin,
} from "../../hooks/applications.hooks";
import { useToggle } from "../../hooks/common";
import { TLevel2PaymentReceipt } from "../../hooks/level-2-payment-approval.hooks";
import { useAllApplicationReviewsAdmin } from "../../hooks/resubmission.hooks";
import { RenderAllReviews } from "../../pages/LevelTwo/RenderAllReviews";
import { capitalize, downloadResource } from "../../utils/common";
import { formatDate } from "../../utils/date";
import { DocumentPreviewDialog } from "../CommonComponents/DialogConfirmation/DocPreviewDialog";
import {
  DownloadButton,
  ViewButton,
} from "../CommonComponents/EditComponent/EditComponent";
import { tableCustomStyles } from "../CommonComponents/Table/Table";
import { ShowField } from "./ViewApplication";
import style from "./ViewApplication.module.css";

export const ViewApplicationDetailsAdmin = () => {
  const [document, setDocument] = useState<{ url: string } | null>(null);
  const location = useLocation();
  const [reviewDialog, toggleReviewDialog] = useToggle();
  const id = location.state?.id;
  const receiptsQuery = usePaymentReceiptsAdmin({ enabled: true, id: +id });
  const allReviewsQuery = useAllApplicationReviewsAdmin({ id: +id });
  const navigate = useNavigate();

  const contentRef = useRef(null);

  const handleDownloadPDF = () => {
    const content = contentRef.current;
    const options = {
      margin: 1,
      filename: "page.pdf",
      image: {
        type: "jpeg",
        quality: 0.98,
      },
      html2canvas: {
        scale: 2,
      },
      jsPDF: {
        unit: "in",
        format: "letter",
        orientation: "portrait",
      },
    };

    html2pdf().from(content).set(options).save();
  };

  const {
    isError: isError1,
    isFetching: isFetching1,
    data: application,
  } = useApplicationAdmin({ id });

  const {
    data: allFields,
    isError: isError3,
    isFetching: isFetching3,
  } = useAllFields({
    serviceName: String(application?.name_of_service || ""),
  });

  const { data: generatedDocs } = useGeneratedDocsAdmin({
    id: +id,
    enabled: true,
  });

  const {
    data: result,
    isError: isError2,
    isFetching: isFetching2,
  } = useApplicationFormSteps(Number(application?.name_of_service));

  const steps = result?.data || [];
  const isFetching =
    isFetching1 || isFetching2 || isFetching3 || receiptsQuery.isLoading;

  const isError =
    isError1 ||
    isError2 ||
    isError3 ||
    receiptsQuery.isError ||
    (!isFetching && (!application || !steps?.length));

  const setStuff = useSetStuffToRenderOnNavbar();

  useEffect(() => {
    setStuff(
      <div className="md:ml-auto md:mr-8">
        <p className="text-grey-600">
          <span className="w-28 font-semibold inline-block">
            Application ID
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span>{application?.applicationId || "Nil"}</span>
        </p>
        <p className="text-grey-600 flex items-center">
          <span className="w-28 text-grey-600 font-semibold inline-block">
            Status
          </span>
          <span className="text-grey-600 font-semibold mx-2">:</span>
          <span className="text-primary">{application?.status || "Nil"}</span>
        </p>
      </div>
    );
    return () => setStuff(null);
  }, [setStuff, application]);

  const applicationStatus = application?.status;

  const receiptsTable = useMemo(
    (): TableColumn<TLevel2PaymentReceipt>[] => [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
      },
      {
        name: "Receipt No",
        selector: (d) => String(d?.receipt_no || "-"),
        center: true,
      },
      {
        name: "Paid Amount",
        selector: (d) => (d?.amount_paid ? `${d.amount_paid} INR` : "-"),
        center: true,
      },
      {
        name: "Payment Date",
        center: true,
        cell: (d) => (d?.payment_date ? formatDate(d.payment_date) : "-"),
      },
      {
        name: "Receipt",
        center: true,
        cell: (d, i) => (
          <div className="flex space-x-4">
            <ViewButton
              onClick={() =>
                setDocument({
                  url: String(d?.receipt_file),
                })
              }
            />
            <DownloadButton
              onClick={() => downloadResource(String(d?.receipt_file))}
            />
          </div>
        ),
      },
    ],
    []
  );

  const nocColumns = useMemo((): TableColumn<Record<string, unknown>>[] => {
    const noc = steps?.find((e) => e.type === "NOC");
    if (!noc) return [];
    return [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        grow: 0.5,
      },
      {
        name: "NOC Type",
        selector: (d) =>
          String(
            (noc.fields?.find((e) => e.uid === "noc_type")?.options || []).find(
              (e) => e.id === d.noc_type
            )?.name || "-"
          ),
        center: true,
        grow: 3,
      },
      {
        name: "NOC No.",
        selector: (d) => String(d?.noc_number || "-"),
        center: true,
        grow: 2,
        width: "120px",
      },
      {
        name: "NOC Date",
        selector: (d) => formatDate(String(d?.noc_date || "x")),
        center: true,
        width: "130px",
      },
      {
        name: "View",
        center: true,
        width: "180px",
        cell: (d, i) => (
          <div className="space-x-4">
            <ViewButton
              onClick={() => setDocument({ url: String(d?.noc_doc) })}
            />
            {application?.status === "Completed" && (
              <DownloadButton
                onClick={() => downloadResource(String(d?.noc_doc))}
              />
            )}
          </div>
        ),
      },
    ];
  }, [steps, applicationStatus]);

  const selectedFields = useMemo(
    () =>
      (steps || [])
        .filter((e) => !["Documents", "NOC"].includes(e.type))
        .flatMap((e) => e.fields || [])
        .reduce<Map<string, true>>((acc, e) => acc.set(e.uid, true), new Map()),
    [steps]
  );

  const generatedDocsColumns = useMemo((): TableColumn<{
    type: string;
    id: number;
    url: string;
    date?: string;
  }>[] => {
    return [
      {
        name: "Sr. No.",
        selector: (_, i) => (i || 0) + 1,
        center: true,
        grow: 0.5,
      },
      {
        name: "Type",
        cell: (d) => capitalize(d.type),
        center: true,
        grow: 3,
      },
      {
        name: "Date",
        selector: (d) => formatDate(String(d?.date || "x")),
        center: true,
        width: "125px",
      },
      {
        name: "View",
        center: true,
        width: "145px",
        cell: (d, i) => (
          <div className="space-x-4">
            <ViewButton onClick={() => setDocument({ url: String(d?.url) })} />
            {/* {application?.status === "Completed" && (
              <DownloadButton
                onClick={() => downloadResource(String(d?.url))}
              />
            )} */}
             <DownloadButton
                onClick={() => downloadResource(String(d?.url))}
              />
          </div>
        ),
      },
    ];
  }, [applicationStatus]);

  const documentTable = useMemo((): {
    docs: { name: string; url: string }[];
    documentColumns: TableColumn<{
      name: string;
      url: string;
    }>[];
  } => {
    const docForm = steps?.find((e) => e.type === "Documents");

    if (!docForm) return { docs: [], documentColumns: [] };
    return {
      docs: Object.entries(application?.documents || {})
        .map(([k, url]) => {
          const hit = docForm?.documentConfigs?.find(
            (e) => e.document_config_id === +k
          )?.document_label;

          if (!hit) return null;
          return { name: hit, url };
        })
        .filter((x): x is NonNullable<typeof x> => !!x),
      documentColumns: [
        {
          name: "Sr. No.",
          selector: (_, i) => (i || 0) + 1,
          center: true,
          grow: 0.5,
        },
        {
          name: "Document Name",
          // selector: (d) => d.name,
          cell: (d) => <span className="text-center">{d.name}</span>,
          center: true,
          grow: 3,
        },
        {
          name: "View",
          center: true,
          width: "145px",
          cell: (d) => (
            <div className="space-x-4">
              <ViewButton
                onClick={() => setDocument({ url: String(d?.url) })}
              />
              {/* {application?.status === "Completed" && (
                <DownloadButton
                  onClick={() => downloadResource(String(d?.url))}
                />
              )} */}
              <DownloadButton
                  onClick={() => downloadResource(String(d?.url))}
                />
            </div>
          ),
        },
      ],
    };
  }, [steps, application]);

  if (isNaN(id)) {
    navigate(-1);
    return null;
  }

  if (isFetching || isError) {
    return (
      <div className="flex justify-center items-center p-8">
        {isError ? (
          <p>Something went wrong while fetching info!</p>
        ) : (
          <SpinnerCircular />
        )}
      </div>
    );
  }

  const PrintData = () => {
    return (
      <div style={{ display: "none" }}>
        <div ref={contentRef}>
          <h4 className="text-center text-grey-600 font-semibold mb-8 text-2xl">
            Application
          </h4>
          <div className="flex flex-col ml-auto mr-8 justify-items-end items-end">
            <p className="text-grey-600">
              <span className="w-28 font-semibold inline-block">
                Application ID
              </span>
              <span className="text-grey-600 font-semibold mx-2">:</span>
              <span>{application?.applicationId || "Nil"}</span>
            </p>
            <p className="text-grey-600 flex items-center">
              <span className="text-grey-600 font-semibold inline-block">
                Status
              </span>
              <span className="text-grey-600 font-semibold mx-2">:</span>
              <span className="text-primary">
                {application?.status || "Nil"}
              </span>
            </p>
          </div>
          <ApplicationList />
        </div>
      </div>
    );
  };

  const ApplicationList = () => {
    let i = 0;
    return (
      <div>
        {(allFields || [])
          .filter((e) => selectedFields.has(e.uid) || !!application?.[e.uid])
          .map((e) => (
            <ShowField
              field={e}
              index={++i}
              key={`field-${e.id}`}
              data={application || {}}
            />
          ))}
      </div>
    );
  };

  return (
    <div className="px-4 pb-4 md:p-4">
      <div className="bg-white p-3 md:p-8">
        <h4 className="text-center text-grey-600 font-semibold mb-4 text-2xl">
          Application
        </h4>
        <div className="flex justify-end mb-4">
          <button
            type="button"
            className="text-base font-bold disabled:text-gray-500"
            disabled={!allReviewsQuery.data?.length}
            onClick={toggleReviewDialog}
          >
            <i className="fa fa-history"></i>
            View All Reviews
          </button>
        </div>
        <ApplicationList />
        <PrintData />
        {!!nocColumns?.length &&
          !!(application?.nocs as Record<string, unknown>[])?.length && (
            <>
              <h4 className="text-base my-4 font-bold text-grey-600">NOC : </h4>
              <DataTable
                keyField="url"
                className="mt-4"
                columns={nocColumns}
                customStyles={tableCustomStyles}
                data={(application?.nocs as Record<string, unknown>[]) || []}
              />
            </>
          )}
        {!!documentTable.docs?.length && (
          <>
            <h4 className="text-base my-4 font-bold text-grey-600">
              Documents :{" "}
            </h4>
            <DataTable
              keyField="url"
              className="mt-4"
              data={documentTable.docs}
              customStyles={tableCustomStyles}
              columns={documentTable.documentColumns}
            />
          </>
        )}
        {!!receiptsQuery?.data?.length && (
          <>
            <h4 className="text-base my-4 font-bold text-grey-600">
              Payment Receipts :{" "}
            </h4>
            <DataTable
              keyField="url"
              className="mt-4"
              columns={receiptsTable}
              data={receiptsQuery?.data}
              customStyles={tableCustomStyles}
            />
          </>
        )}
        {!!generatedDocs?.length && (
          <>
            <h4 className="text-base my-4 font-bold text-grey-600">
              Application Documents :{" "}
            </h4>
            <DataTable
              keyField="url"
              className="mt-4"
              data={generatedDocs}
              columns={generatedDocsColumns}
              customStyles={tableCustomStyles}
            />
          </>
        )}
        <div className={`${style.buttonSection} gap-5`}>
          <button className={style.closeButton} onClick={() => navigate(-1)}>
            Back
          </button>
          <button
            type="button"
            onClick={handleDownloadPDF}
            className="bg-primary px-6 py-2 rounded-full font-bold text-white"
          >
            Download
          </button>
        </div>
      </div>
      {/* </div> */}
      {reviewDialog && (
        <RenderAllReviews
          title="Reviews"
          onClose={toggleReviewDialog}
          reviews={allReviewsQuery.data ?? []}
        />
      )}

      {document && (
        <DocumentPreviewDialog
          onClose={() => setDocument(null)}
          open={!!document}
        >
          {document.url.endsWith("pdf") ? (
            <Worker
              workerUrl={`https://unpkg.com/pdfjs-dist@2.16.105/build/pdf.worker.min.js`}
            >
              <Viewer fileUrl={document.url} />
            </Worker>
          ) : (
            <img
              src={document.url}
              className="object-contain w-full h-full"
              alt="Document"
            />
          )}
        </DocumentPreviewDialog>
      )}
    </div>
  );
};
